import React, { Fragment, ReactElement, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Typography,
  Checkbox,
  Avatar,
  MenuItem,
  Collapse,
  Box,
  TextField
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Done as DoneIcon,
  Tune as TuneIcon,
  VerticalAlignBottom as ResultIcon,
  SupervisedUserCircle as ReassignUserIcon
} from '@mui/icons-material'
import { Pagination } from '../../../components/Pagination'
import { Loading } from '../../UI'
import Menu from '@mui/material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  ActionProps,
  GenericTablePropsExpand,
  PrioritizationActionProps,
  TableData
} from './types'
import { getImage } from './helpers'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const useStyles = makeStyles((theme) => ({
  action: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10vh'
  },
  tableHeaderCell: {},
  sortable: {
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      cursor: 'pointer',
      transition: 'background-color 0.3s ease-in-out'
    }
  },
  selected: {
    backgroundColor: theme.palette.grey[300]
  },
  imageLarge: {
    width: theme.spacing(6),
    height: theme.spacing(6)
  },
  expandButton: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest
    })
  },
  expandedContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.grey[300]}`
  },
  detailHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      transition: 'background-color 0.3s ease-in-out'
    }
  }
}))

export const GenericTableExpand = <T extends TableData, D = any>({
  data = [],
  pagination,
  onPaginationChange,
  headersConfig,
  rowConfig = [],
  actions,
  showCheckboxes = false,
  onCheckChange,
  checkedElements = [],
  onCheckAll,
  dense = false,
  IDKey,
  customActionComponent: CustomActionComponent,
  isLoading,
  onSortChange,
  orderField,
  showPriorityMenu = false,
  priorization,
  noResultsText,
  priorizationNumberKey,
  expandable,
  customExtraColumnsCount,
  customNameExpandable = 'Detalle',
  customExpandIcon,
  noDetailsComponent: NoDetailsComponent
}: GenericTablePropsExpand<T, D>) => {
  const styles = useStyles()
  const [editedValues, setEditedValues] = useState<Record<string, any>>({})
  // Función helper para crear IDs únicos
  const createUniqueId = (rowData: T, index: number): string => {
    // Si IDKey existe y tiene valor, usarlo directamente SIN agregar índice
    if (IDKey && rowData[IDKey] !== undefined && rowData[IDKey] !== null) {
      return String(rowData[IDKey])
    }

    // Fallback: Usar solo el índice
    return `row-${index}`
  }

  // Función helper para verificar si una fila tiene detalles disponibles
  const hasDetails = (rowData: T): boolean => {
    if (!expandable?.getDetailData) return false

    const detailData = expandable.getDetailData(rowData)

    // Verificar si hay datos de detalle
    if (!detailData) return false

    // Si es un array, verificar que no esté vacío
    if (Array.isArray(detailData)) {
      return detailData.length > 0
    }

    // Si es un objeto, verificar que no esté vacío
    if (typeof detailData === 'object') {
      return Object.keys(detailData).length > 0
    }

    // Para otros tipos de datos, considerar que hay detalles si no es null/undefined
    return true
  }

  // Estado para controlar las filas expandidas usando Set
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  )

  // Estado para controlar si todas las filas están expandidas
  const [allRowsExpanded, setAllRowsExpanded] = useState<boolean>(false)

  const [anchorEls, setAnchorEls] = useState<
    Record<string | number, HTMLElement | null>
  >({})

  // Función para expandir/contraer todas las filas
  const toggleAllRows = () => {
    if (allRowsExpanded) {
      // Contraer todas las filas
      setExpandedRows(new Set())
      setAllRowsExpanded(false)
    } else {
      // Expandir solo las filas que tienen detalles
      const newExpandedRows = new Set<string | number>()
      data.forEach((rowData, index) => {
        if (hasDetails(rowData)) {
          const rowId = createUniqueId(rowData, index)
          newExpandedRows.add(rowId)
        }
      })
      setExpandedRows(newExpandedRows)
      setAllRowsExpanded(true)
    }
  }

  // Función mejorada para manejar la expansión con debugging
  const toggleRowExpansion = (id: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }

      // Actualizar el estado de todas las filas expandidas
      const rowsWithDetails = data.filter(hasDetails).length
      const expandedRowsCount = newSet.size
      setAllRowsExpanded(
        expandedRowsCount > 0 && expandedRowsCount === rowsWithDetails
      )

      return newSet
    })
  }

  // Efecto para contraer todas las filas cuando cambia la página
  React.useEffect(() => {
    setExpandedRows(new Set())
    setAllRowsExpanded(false)
  }, [pagination?.page, data.length]) // Se ejecuta cuando cambia la página o se recarga con nuevos datos

  // Función para verificar si una fila está expandida
  const isRowExpanded = (id: string | number) => {
    return expandedRows.has(id)
  }

  // Selección múltiple
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheckAll) {
      onCheckAll(event.target.checked)
    }
  }

  // Renderizado de celdas de acción
  const renderActionsCell = (id: string | number, row: T) => {
    if (CustomActionComponent) {
      return (
        <TableCell align='center'>
          <div className={styles.action}>
            <CustomActionComponent id={id as number} row={row} />
          </div>
        </TableCell>
      )
    }

    const {
      onDelete,
      onEdit,
      onView,
      onConfirm,
      onTune,
      onResult,
      onReassign
    } = actions as ActionProps<T>

    return (
      <TableCell align='center'>
        <div className={styles.action}>
          {onTune && (
            <IconButton onClick={() => onTune(id as number, row)}>
              <TuneIcon />
            </IconButton>
          )}
          {onEdit && (
            <IconButton onClick={() => onEdit(id as number, row)}>
              <EditIcon />
            </IconButton>
          )}
          {onReassign && (
            <IconButton onClick={() => onReassign(id as number, row)}>
              <ReassignUserIcon />
            </IconButton>
          )}
          {onView && (
            <IconButton onClick={() => onView(id as number, row)}>
              <AssignmentIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton onClick={() => onDelete(id as number, row)}>
              <DeleteIcon />
            </IconButton>
          )}
          {onConfirm && (
            <IconButton onClick={() => onConfirm(id as number, row)}>
              <DoneIcon />
            </IconButton>
          )}
          {onResult && (
            <IconButton onClick={() => onResult(id as number, row)}>
              <ResultIcon />
            </IconButton>
          )}
        </div>
      </TableCell>
    )
  }

  // Renderizado de checkbox
  const renderCheckboxCell = (id: string | number) => (
    <TableCell align='center'>
      <Checkbox
        checked={checkedElements.includes(id as number)}
        onChange={(event) =>
          onCheckChange?.(id as number, event.target.checked)
        }
      />
    </TableCell>
  )

  const renderCellValue = (
    value: any,
    formater?: (value: any, row?: T) => string | ReactElement,
    isImage?: boolean,
    row?: T,
    field?: keyof T,
    rowId?: string | number,
    editable?: boolean | ((row: T) => boolean), // MODIFICADO: ahora acepta función
    inputType?: 'text' | 'number',
    onEditChange?: (rowId: string | number, newValue: any) => void
  ): React.ReactNode => {
    const uniqueKey =
      field !== undefined && rowId !== undefined
        ? `${rowId}-${String(field)}`
        : ''

    // NUEVO: Determinar si es editable evaluando la función si es necesario
    const isEditable =
      typeof editable === 'function' ? (row ? editable(row) : false) : editable

    if (isEditable) {
      const currentValue =
        editedValues[uniqueKey] !== undefined ? editedValues[uniqueKey] : value

      return (
        <TextField
          value={
            currentValue !== null && currentValue !== undefined
              ? currentValue
              : ''
          }
          onChange={(e) => {
            let newValue: any = e.target.value

            if (inputType === 'number') {
              if (newValue === '') {
                newValue = null
              } else {
                const numValue = Number(newValue)
                if (!isNaN(numValue)) {
                  newValue = numValue
                } else {
                  return
                }
              }
            }

            setEditedValues((prev) => ({ ...prev, [uniqueKey]: newValue }))

            if (onEditChange) {
              onEditChange(rowId!, newValue)
            }
          }}
          type={inputType === 'number' ? 'number' : 'text'}
          inputProps={inputType === 'number' ? { min: 0, step: 1 } : undefined}
          variant='outlined'
          size='small'
          fullWidth
        />
      )
    }

    if (formater) return formater(value, row)
    if (typeof value === 'boolean') return value ? 'Activo' : 'Inactivo'
    if (isImage) {
      return (
        <Avatar
          className={styles.imageLarge}
          variant='square'
          alt='SkuImage'
          src={getImage(value)}
        />
      )
    }
    return value
  }

  // Menú de prioridad
  const handlePriorityMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: T
  ) => {
    setAnchorEls((prev) => ({ ...prev, [row[IDKey]]: event.currentTarget }))
  }

  const handlePriorityMenuClose = (rowId: string | number) => {
    setAnchorEls((prev) => ({ ...prev, [rowId]: null }))
  }

  const calculatePriorityNumber = (
    rowIndex: number,
    currentPage: number,
    pageSize: number
  ) => {
    return currentPage * pageSize + rowIndex + 1
  }

  const renderExpandIcon = (isExpanded: boolean) => {
    if (customExpandIcon) {
      const CustomIcon = customExpandIcon
      return <CustomIcon expanded={isExpanded} />
    }
    return isExpanded ? (
      <ExpandLessIcon fontSize='small' />
    ) : (
      <ExpandMoreIcon fontSize='small' />
    )
  }
  const renderPriorityMenu = (row: T) => {
    const { onTop, onUp, onDown } = priorization as PrioritizationActionProps<T>
    const priorityNumber = priorizationNumberKey
      ? row[priorizationNumberKey]
      : calculatePriorityNumber(
          data.indexOf(row),
          pagination?.page || 0,
          pagination?.pageSize || 0
        )

    return (
      <TableCell align='center'>
        <IconButton onClick={(event) => handlePriorityMenuClick(event, row)}>
          {priorityNumber > 0 && <span>{priorityNumber}</span>}
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEls[row[IDKey]]}
          keepMounted
          open={Boolean(anchorEls[row[IDKey]])}
          onClose={() => handlePriorityMenuClose(row[IDKey])}
        >
          {onTop && (
            <MenuItem
              onClick={() => {
                onTop(row[IDKey], row)
                handlePriorityMenuClose(row[IDKey])
              }}
            >
              Mover al principio
            </MenuItem>
          )}
          {onUp && (
            <MenuItem
              onClick={() => {
                onUp(row[IDKey], row)
                handlePriorityMenuClose(row[IDKey])
              }}
            >
              Mover arriba
            </MenuItem>
          )}
          {onDown && (
            <MenuItem
              onClick={() => {
                onDown(row[IDKey], row)
                handlePriorityMenuClose(row[IDKey])
              }}
            >
              Mover abajo
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.empty}>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={styles.empty}>
        <Typography>
          {noResultsText ||
            'Ajuste los criterios de búsqueda para desplegar resultados'}
        </Typography>
      </div>
    )
  }

  // Calcular el colSpan correctamente
  const totalColumns =
    headersConfig.length +
    (showCheckboxes ? 1 : 0) +
    (showPriorityMenu ? 1 : 0) +
    (actions || CustomActionComponent ? 1 : 0) +
    (expandable?.showExpandButton ? 1 : 0)

  const colSpanCount =
    customExtraColumnsCount !== undefined
      ? customExtraColumnsCount
      : totalColumns

  return (
    <TableContainer>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {showCheckboxes && (
              <TableCell align='center'>
                <Checkbox
                  indeterminate={
                    checkedElements.length > 0 &&
                    checkedElements.length < data.length
                  }
                  checked={
                    checkedElements.length === data.length && data.length > 0
                  }
                  onChange={handleSelectAllClick}
                />
              </TableCell>
            )}
            {showPriorityMenu && (
              <TableCell align='center' style={{ width: '50px' }} />
            )}
            {headersConfig.map((config) => (
              <TableCell
                key={String(config.field)}
                align='center'
                style={{ ...config.headStyle, position: 'relative' }}
                onClick={() =>
                  config.sortable &&
                  onSortChange?.({
                    field: config.fieldReplacement || config.field,
                    direction: 'asc'
                  })
                }
                className={`${styles.tableHeaderCell} ${
                  config.sortable ? styles.sortable : ''
                } ${
                  orderField?.field === config.field && config.sortable
                    ? styles.selected
                    : ''
                }`}
              >
                {config.title}
                {orderField?.field === config.field && config.sortable && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      right: '4px'
                    }}
                  >
                    {orderField.direction === 'asc' ? (
                      <ExpandLessIcon fontSize='small' />
                    ) : (
                      <ExpandMoreIcon fontSize='small' />
                    )}
                  </span>
                )}
              </TableCell>
            ))}
            {(actions || CustomActionComponent) && (
              <TableCell align='center' style={{ width: '50px' }}>
                Acciones
              </TableCell>
            )}
            {expandable?.showExpandButton && (
              <TableCell
                align='center'
                style={{ width: '50px' }}
                className={styles.detailHeader}
                onClick={toggleAllRows}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {customNameExpandable}{' '}
                  {allRowsExpanded ? (
                    <ExpandLessIcon fontSize='small' />
                  ) : (
                    <ExpandMoreIcon fontSize='small' />
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((rowData, index) => {
            // Usar la función helper para crear ID único
            const rowId = createUniqueId(rowData, index)
            const detailData = expandable?.getDetailData?.(rowData)
            const isExpanded = isRowExpanded(rowId)
            const rowHasDetails = hasDetails(rowData)

            return (
              <React.Fragment key={`row-${rowId}`}>
                <TableRow
                  style={{ height: dense ? 33 : 53, ...rowConfig[index] }}
                >
                  {showCheckboxes && renderCheckboxCell(rowId)}
                  {showPriorityMenu && renderPriorityMenu(rowData)}
                  {headersConfig.map((config) => (
                    <TableCell
                      key={String(config.field)}
                      align='center'
                      style={config.style}
                    >
                      {renderCellValue(
                        rowData[config.field],
                        config.formater,
                        config.isImage,
                        rowData,
                        config.field,
                        rowId,
                        config.editable,
                        config.inputType,
                        config.onEditChange
                      )}
                    </TableCell>
                  ))}
                  {(actions || CustomActionComponent) &&
                    renderActionsCell(rowId, rowData)}
                  {expandable?.showExpandButton && (
                    <TableCell align='center'>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: '4px',
                          paddingLeft: '10px'
                        }}
                      >
                        {!rowHasDetails && NoDetailsComponent ? (
                          <NoDetailsComponent row={rowData} />
                        ) : !rowHasDetails ? (
                          <Box style={{ width: '24px' }}></Box>
                        ) : null}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleRowExpansion(rowId)
                          }}
                          className={styles.expandButton}
                          size='small'
                          disabled={!rowHasDetails}
                        >
                          {renderExpandIcon(isExpanded)}
                        </IconButton>
                      </div>
                    </TableCell>
                  )}
                </TableRow>

                {/* Mover la fila expandida fuera del condicional */}
                {expandable?.showExpandButton && (
                  <TableRow key={`expanded-${rowId}`}>
                    <TableCell
                      colSpan={colSpanCount}
                      style={{
                        padding: 0,
                        borderBottom: 'none',
                        border: isExpanded
                          ? '1px solid rgba(224, 224, 224, 1)'
                          : 'none'
                      }}
                    >
                      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                        <div className={styles.expandedContent}>
                          {detailData ? (
                            <Fragment>
                              {expandable.renderDetail(detailData, rowData)}
                            </Fragment>
                          ) : (
                            <Typography variant='body2'>
                              No hay detalles disponibles
                            </Typography>
                          )}
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
      <Pagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
      />
    </TableContainer>
  )
}
